import {
  FunctionComponent,
  createContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { removeStudyPlanEnrollmentDefault } from '../components/careerSelector/studyPlanEnrollmentDefault';
import Loading from '../components/info/Loading';
import SessionExpiredModal from '../components/sessionExpiredModal/SessionExpiredModal';
import { useI18nFullFilled } from '../hooks/useI18nFullFilled';
import userReducer, { DEFAULT_VALUE } from '../reducers/userReducer';
import { UserSessionEvent } from '../types/Auth';
import { UserActionTypes, UserContextType } from '../types/userTypes';
import { getJWT, parseJWT, removeJWT } from '../utils/auth';

export const UserContext = createContext({} as UserContextType);

const UserProvider: FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, DEFAULT_VALUE);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  const i18FullFilled = useI18nFullFilled();

  // Manejo de datos de sesion tomados del token
  useEffect(() => {
    const handleStorage = () => {
      const token = getJWT();

      if (token) {
        const data = parseJWT(token);
        const nowSeconds = Math.floor(Date.now() / 1000);
        if (typeof data?.exp === 'number' && data.exp <= nowSeconds) {
          removeJWT();
          dispatch({ type: UserActionTypes.RESET_STATE });
          return;
        }
        dispatch({
          type: UserActionTypes.SET_USER_DATA,
          payload: { ...data?.account, permissions: data?.permissions },
        });
        dispatch({ type: UserActionTypes.SET_IS_LOGGED, payload: true });
      } else {
        dispatch({ type: UserActionTypes.RESET_STATE });
      }
    };

    // Primer chequeo al cargar la app
    handleStorage();

    // Para escuchar en el futuro los cambios del token
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Manejo de expiracion de sesion
  useEffect(() => {
    const notifySessionExpiration = () => {
      setShowSessionExpiredModal(true);
      removeStudyPlanEnrollmentDefault();
    };

    window.addEventListener(UserSessionEvent.EXPIRED, notifySessionExpiration);

    return () => {
      window.removeEventListener(
        UserSessionEvent.EXPIRED,
        notifySessionExpiration,
      );
    };
  }, []);

  if (!i18FullFilled) {
    return (
      <div className="vh-100 d-flex">
        <div className="m-auto">
          <Loading textBody=" " />
        </div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      <>
        <SessionExpiredModal
          isOpen={showSessionExpiredModal}
          onConfirm={() => {
            dispatch({ type: UserActionTypes.RESET_STATE });
            setShowSessionExpiredModal(false);
          }}
        />
        {children}
      </>
    </UserContext.Provider>
  );
};

export default UserProvider;
